import { Directive, OnInit, ViewChild } from '@angular/core';
import {
  createForm,
  createFormArray,
  createFormControl,
  DestroyableBase,
  FormControls,
  MapKeyTypesTo,
  SubFormArray,
  TypedFormArray,
  TypedFormGroup,
} from 'osn-shared';
import { Observable, of } from 'rxjs';
import { DialogComponent } from '../dialog/dialog.component';
import { PageDetailDialogDataViewModel } from './page-detail-dialog-data.view-model';
import { PageDetailDialogModalViewModel } from './page-detail-dialog-modal.view-model';
import { PageDialogVariant } from './page-dialog-variant';

type FormModel<T extends PageDetailDialogDataViewModel> = Omit<T, 'metaTags'> &
  MapKeyTypesTo<Pick<T, 'metaTags'>, { key: string; value: string }[]>;

type SimpleControls<T extends PageDetailDialogDataViewModel> = FormControls<Omit<FormModel<T>, 'metaTags'>>;
type ArrayControls<T extends PageDetailDialogDataViewModel> = SubFormArray<Pick<FormModel<T>, 'metaTags'>>;

@Directive()
export abstract class PageDetailDialogComponent<T extends PageDetailDialogDataViewModel, S = any>
  extends DestroyableBase
  implements OnInit
{
  @ViewChild(DialogComponent) private readonly dialog: DialogComponent;

  public formControls: SimpleControls<T> & ArrayControls<T>;
  public form: TypedFormGroup<FormModel<T>>;

  public PageDialogVariant = PageDialogVariant;

  protected constructor(public data: PageDetailDialogModalViewModel<T>) {
    super();
  }

  public ngOnInit(): void {
    this._initForm();

    if (
      this.data.variant === PageDialogVariant.NEW ||
      (this.data.variant === PageDialogVariant.EDIT && !Object.entries(this.data.data.metaTags).length)
    ) {
      this.addNewMetaTag();
    }

    if (this.data.variant === PageDialogVariant.READ_ONLY) {
      this.form.disable();
    }
  }

  public onSaveClick(): void {
    this.validateFormAndClose();
  }

  public addNewMetaTag(key = '', value = '') {
    this.metaTags.insert(this.metaTags.length, createForm({ key: createFormControl(key), value: createFormControl(value) }));
  }

  public get metaTags() {
    return this.formControls.metaTags;
  }

  public get dialogTitle() {
    switch (this.data.variant) {
      case PageDialogVariant.NEW:
        return 'Create new page';
      case PageDialogVariant.EDIT:
        return 'Edit page';
      case PageDialogVariant.READ_ONLY:
        return 'View page details';
    }
  }

  public get dialogAction() {
    switch (this.data.variant) {
      case PageDialogVariant.NEW:
        return 'Create page';
      case PageDialogVariant.EDIT:
        return 'Update page';
      case PageDialogVariant.READ_ONLY:
        return 'Close';
    }
  }

  protected loadAdditionalParameters$(): Observable<S> {
    return of(undefined);
  }

  private _initForm(): void {
    const metaTagsValue = Object.entries(this.data.data?.metaTags ?? {}).map(([key, value]) => ({ key, value }));
    const metaTagsControl = createFormArray(
      metaTagsValue.map((it) => createForm({ key: createFormControl(it.key), value: createFormControl(it.value) }))
    );

    this.loadAdditionalParameters$().subscribe((additionalParams) => {
      this.initForm(metaTagsControl, additionalParams);
    });
  }

  private validateFormAndClose(): void {
    if (this.data.variant === PageDialogVariant.READ_ONLY) {
      this.dialog.closeDialog(undefined);
      return;
    }

    this.form.updateValueAndValidity();

    if (this.form.invalid) {
      return;
    }

    this.dialog.closeDialog(this.getEmittedResult());
  }

  protected getEmittedResult(): T {
    // @ts-ignore
    return {
      ...this.form.value,
      // @ts-ignore
      metaTags: Object.fromEntries(
        this.form.value.metaTags.map(({ key, value }) => [key, value]).filter(([key, value]) => !!key && !!value)
      ) as Record<string, string>,
    };
  }

  protected abstract initForm(metaTagsControl: TypedFormArray<{ key: string; value: string }>, additionalParams?: S);
}
